import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React, { useContext } from 'react';

import { InsuranceFormValues, StringKeySignature } from '@/components/InsuranceSection/types';
import InPlaceEditor from '@/components/PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import { RecordType } from '@/components/PersonInPlaceEditor/components/InPlaceEditor/types';
import { Field } from '@/components/PersonInPlaceEditor/types';
import useInsuranceVerificationFields from '@/components/PersonInsurancePolicy/InsuranceInPlaceEditor/useInsuranceVerificationFields';
import { PictureUploadKind } from '@/constants/s3DirectFileUpload';
import AppContext from '@/contexts/appContext';
import useUploadImage from '@/hooks/useUploadImage';
import PersonContext from '@/pages/Person/PersonContext';
import { insuranceSectionSchema } from '@/pages/Profile/schemas';
import InsuranceRepository from '@/repositories/InsuranceRepository';
import { InsurancePolicy } from '@/types/insurancePolicy';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import type { Props } from './types';

const InsuranceInPlaceEditor = ({ children, insurancePolicy }: Props) => {
  const insuranceRepository = InsuranceRepository();
  const { addMessage, host } = useContext(AppContext);
  const {
    person: { id: personId },
    refetch: refetchPerson
  } = useContext(PersonContext);

  const { getUploadConfig, uploadFile } = useUploadImage({ addMessage, host });

  const insurancePolicyMutationPromise = async ({
    id,
    values
  }: {
    id?: string;
    values: Partial<InsurancePolicy>;
  }) => {
    if (!Object.keys(values).length) {
      return;
    }

    if (!id) {
      return await insuranceRepository.createInsurancePolicy(values);
    }

    return await insuranceRepository.updateInsurancePolicy(id, values);
  };

  const mutationFn = async ({
    id,
    values
  }: {
    id?: string;
    values: Partial<InsuranceFormValues>;
  }) => {
    if (!values) {
      return;
    }
    return await insurancePolicyMutationPromise({ id, values });
  };

  const mutateInsurancePolicy = useMutation({
    mutationFn,
    onSuccess: async (response) => {
      if (!response) return;
      await refetchPerson();
      addMessage('success', 'Insurance Policy saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deleteInsurancePolicy = useMutation({
    mutationFn: (id: string) => insuranceRepository.deleteInsurancePolicy(id),
    onSuccess: async () => {
      await refetchPerson();
      addMessage('success', 'Insurance Policy deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submittedValuesManipulator = async (values: Partial<InsurancePolicy>) => {
    const pictureUploadKindMap: { [key: string]: PictureUploadKind } = {
      front_image_url: PictureUploadKind.insuranceCardFrontImage,
      back_image_url: PictureUploadKind.insuranceCardBackImage
    };
    const insuranceCardKeys = ['front_image_url', 'back_image_url'];
    if (insuranceCardKeys.some((key) => Object.keys(values).includes(key))) {
      for (const key of insuranceCardKeys) {
        if (Object.keys(values).includes(key)) {
          const value = (values as StringKeySignature)[key];
          if (value instanceof File) {
            delete (values as StringKeySignature)[key];

            try {
              const config = await getUploadConfig(
                {
                  contentType: value.type,
                  pictureKind: pictureUploadKindMap[key] as PictureUploadKind
                },
                personId
              );

              if (!config) {
                addMessage('error', `Failed to upload insurance card (${key}).`);
                continue;
              }

              const uploadStatus = await uploadFile(value, config);

              // uploadToS3 returns a status of 204 if successful
              if (
                uploadStatus?.status &&
                200 <= uploadStatus.status &&
                uploadStatus.status <= 299
              ) {
                const uploadedFileName = config.fields.key;
                if (values['insurance_card_images']) {
                  (values['insurance_card_images'] as StringKeySignature)[key] = uploadedFileName;
                } else {
                  values['insurance_card_images'] = {
                    [key]: uploadedFileName
                  };
                }
              } else {
                addMessage('error', `Failed to upload insurance card (${key}).`);
              }
            } catch (error) {
              addMessage('error', `Failed to upload insurance card (${key}). ${error}`);
            }
          }
        }
      }
    }

    return values;
  };

  return (
    <div key={insurancePolicy.id} className="group/subsection mb-4 relative">
      <InPlaceEditor
        deleteMutation={deleteInsurancePolicy}
        disableEdit={mutateInsurancePolicy.isPending || deleteInsurancePolicy.isPending}
        editButtonClass="top-6"
        fields={
          useInsuranceVerificationFields({
            personIdField: true,
            insuranceCardImages: insurancePolicy.insurance_card_images
          }) as Field<InsurancePolicy>[]
        }
        key={insurancePolicy.id}
        mutation={mutateInsurancePolicy}
        record={
          {
            ...insurancePolicy,
            co_insurance: insurancePolicy.co_insurance ? insurancePolicy.co_insurance * 100 : null,
            co_pay: insurancePolicy.co_pay ? insurancePolicy.co_pay / 100 : null,
            out_of_pocket_maximum: insurancePolicy.out_of_pocket_maximum
              ? insurancePolicy.out_of_pocket_maximum / 100
              : null
          } as InsurancePolicy
        }
        recordType={RecordType.InsurancePolicy}
        schema={insuranceSectionSchema}
        submittedValuesManipulator={submittedValuesManipulator}
        subsectionEditor>
        {children}
      </InPlaceEditor>
    </div>
  );
};

export default React.memo(InsuranceInPlaceEditor);
